import { useState } from "react";
import type { FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Aviso, Botao, Campo } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { segredoEmBlocos, type Inscricao } from "../../nucleo/acesso";
import { CodigosDeRecuperacao } from "./CodigosDeRecuperacao";
import { QrDoSegredo } from "./QrDoSegredo";

export interface PropsDoConfigurarSegundoFator {
  /** Chave da consulta do segredo — uma por contexto (login ou conta). */
  chave: readonly unknown[];
  iniciar: () => Promise<Inscricao>;
  /** Prova o primeiro código e devolve os códigos de recuperação (únicos em claro). */
  confirmar: (codigo: string) => Promise<string[]>;
  aoConcluir: () => void;
  rotuloDoFim?: string;
}

/**
 * Configurar o aplicativo autenticador: QR (ou chave para digitar), primeiro código, e então os
 * códigos de recuperação. É o mesmo componente no login (quando a política exige) e na conta.
 */
export function ConfigurarSegundoFator({
  chave,
  iniciar,
  confirmar,
  aoConcluir,
  rotuloDoFim,
}: PropsDoConfigurarSegundoFator) {
  // O segredo não pode ser buscado de novo no meio do caminho: cada pedido ao servidor gera um
  // segredo NOVO (e invalida o QR anterior), então refazer a consulta a cada foco da janela
  // deixaria o aplicativo de quem está com o celular na mão com um QR que já não vale.
  const inscricao = useQuery({
    queryKey: chave,
    queryFn: iniciar,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [codigosDeRecuperacao, setCodigosDeRecuperacao] = useState<string[] | null>(null);

  async function conferir(valor: string) {
    setErro(null);
    setEnviando(true);
    try {
      setCodigosDeRecuperacao(await confirmar(valor.trim()));
    } catch (causa) {
      setErro(mensagemDoConsole(causa));
    } finally {
      setEnviando(false);
    }
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    void conferir(codigo);
  }

  if (codigosDeRecuperacao) {
    return (
      <CodigosDeRecuperacao
        codigos={codigosDeRecuperacao}
        rotuloDoFim={rotuloDoFim}
        aoContinuar={aoConcluir}
      />
    );
  }
  if (inscricao.isPending) return <p role="status">Preparando a configuração…</p>;
  if (inscricao.isError) return <Aviso tom="erro">{mensagemDoConsole(inscricao.error)}</Aviso>;

  return (
    <form className="passo-do-mfa" onSubmit={enviar} noValidate>
      <ol className="acesso__lista" aria-label="Como configurar">
        <li>
          Instale um aplicativo autenticador no celular (Google Authenticator, Microsoft
          Authenticator, Authy ou 1Password).
        </li>
        <li>Leia o QR code abaixo com o aplicativo.</li>
        <li>Digite aqui o código de 6 dígitos que o aplicativo mostrar.</li>
      </ol>
      <QrDoSegredo uri={inscricao.data.uri} />
      <div>
        <p className="campo__ajuda m-0 mb-1">Sem câmera? Digite esta chave no aplicativo:</p>
        <div className="segredo-manual" data-testid="segredo-manual">
          {segredoEmBlocos(inscricao.data.segredo)}
        </div>
      </div>
      <Campo
        rotulo="Código do aplicativo"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="000000"
        value={codigo}
        onChange={(evento) => {
          const valor = evento.target.value;
          setCodigo(valor);
          if (/^\d{6}$/.test(valor.trim())) void conferir(valor);
        }}
        disabled={enviando}
        data-testid="codigo-de-configuracao"
        obrigatorio
      />
      {erro ? (
        <Aviso tom="erro" data-testid="erro-do-segundo-fator">
          {erro}
        </Aviso>
      ) : null}
      <Botao type="submit" variante="verde" disabled={enviando || codigo.trim().length === 0}>
        {enviando ? "Conferindo…" : "Confirmar e ativar"}
      </Botao>
    </form>
  );
}
